// ─────────────────────────────────────────────────────────────────────────────
// parseXPodFrame.js — XBoat XPod oarlock per-stroke frame parser (STUB)
//
// Mirrors `nkParseStrokeFrame.js` shape so the rest of the app (debug panel,
// useOarlockCalibration, mergedOarlockData) can consume XPod data unchanged
// once the GATT byte layout is documented by XBoat.
//
// TODO: confirm with XBoat docs — fill in:
//   • notification characteristic UUID
//   • frame length (currently unknown)
//   • byte offsets / scales for each field
//   • null sentinels per field
//
// Until then this function ALWAYS returns null so any plumbing that calls it
// is exercised end-to-end without inventing fake data.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @typedef {object} XPodStrokeData
 * @property {number} timestampMs         — stroke timestamp (ms, wraps ~65s)
 * @property {number} strokePeriodMs      — full stroke period in ms
 * @property {number} spm                 — strokes per minute (derived)
 * @property {number} driveTimeMs         — drive phase duration (ms)
 * @property {number} recoveryTimeMs      — recovery phase duration (ms, derived)
 * @property {number} peakForceN          — peak force in Newtons
 * @property {number} avgForceN           — average force in Newtons
 * @property {number} workPerStrokeJ      — work per stroke (Joules)
 * @property {number} powerW              — oarlock power (Watts)
 * @property {number} driveLengthCm       — drive length (cm)
 * @property {number} catchAngleDeg       — catch angle, negative convention (e.g. −47°)
 * @property {number|null} finishAngleDeg — finish angle, positive (nullable)
 * @property {number|null} effectiveLengthDeg — effective arc in degrees (nullable)
 * @property {boolean} isValid            — false if frame is a glitch / transition
 * @property {'port'|'starboard'} side    — which oarlock produced this frame
 */

/**
 * Parse a single XPod notification payload.
 *
 * @param {DataView|Uint8Array|ArrayBuffer} _data
 * @param {'port'|'starboard'} _side — caller-tagged side based on connection slot
 * @returns {XPodStrokeData|null} — currently always null (stub)
 */
// eslint-disable-next-line no-unused-vars
export function parseXPodFrame(_data, _side = 'port') {
  // TODO: implement once XBoat publishes the dataStream byte layout.
  //
  // Expected steps:
  //   1. Validate frame length matches the documented size.
  //   2. Decode each u16 / u8 field with the published scale + endianness.
  //   3. Apply null sentinels per field.
  //   4. Derive strokePeriodMs → spm and driveTimeMs → recoveryTimeMs.
  //   5. Compute `isValid` against documented sanity rules.
  //   6. Return the populated XPodStrokeData object including the `side` tag.
  return null;
}
